import {
  ADMIN_DISPLAY_VALUE,
  ADMIN_GROUP,
  CLIENT_OWNER_DISPLAY_VALUE,
  CLIENT_OWNER_GROUP,
  PENTESTER_DISPLAY_VALUE,
  PENTESTER_GROUP,
} from "@/constants/definitions";
import { GraphQLResponse } from "@/types/interfaces";

export function convertRoleToFrontEnd(roleList: string[]): string {
  let role = roleList.join(", ");
  role = role.replace(CLIENT_OWNER_GROUP, CLIENT_OWNER_DISPLAY_VALUE);
  role = role.replace(ADMIN_GROUP, ADMIN_DISPLAY_VALUE);
  role = role.replace(PENTESTER_GROUP, PENTESTER_DISPLAY_VALUE);
  return role;
}

// Was Graph Call Successful
// --------------------------
// Only works for responses with a specific structure
//
// Response Examples
// -------------------
// TODO: Need to Fix the response such that a response to a deleted message is the same as for a non existant one
//
// Successful response
// {
//     "data": {
//         "getWebApplication": {
//             "id": "f7ca5cfe-580a-4102-b5db-c18297b5cd13"
//         }
//     }
// }
//
// Unauthorized (User is legit but the app is not theirs)
// {
//     "data": {
//         "getWebApplication": null
//     },
//     "errors": [
//         {
//         "path": [
//             "getWebApplication"
//         ],
//         "data": null,
//         "errorType": "Unauthorized",
//         "errorInfo": null,
//         "locations": [
//             {
//                 "line": 9,
//                 "column": 8,
//                 "sourceName": null
//             }
//         ],
//         "message": "Not Authorized to access getWebApplication on type Query"
//         }
//     ]
// }
//
// Query when web app does not exist
// {
//     "data": {
//         "getWebApplication": null
//     }
// }
//
// Response when app exist but is deleted
// {
//     "data": {
//         "getWebApplication": null
//     },
//     "errors": [
//         {
//             "path": [
//               "getWebApplication"
//             ],
//             "data": null,
//             "errorType": null,
//             "errorInfo": null,
//             "locations": [
//                 {
//                     "line": 9,
//                     "column": 8,
//                     "sourceName": null
//                 }
//             ],
//             "message": "Not found"
//         }
//     ]
// }
//
export function getGraphData(
  response: GraphQLResponse,
  requestName: string
): any {
  if (response) {
    // Surface GraphQL errors so partial-failure cases (e.g. resolver returning
    // a partial response with errors, or scalar validation rejecting one field)
    // are diagnosable rather than silently nulling the consumer.
    if (response.errors) {
      console.error(`[GraphQL errors in ${requestName}]`, response.errors);
      return false;
    }
    // If the response data exists then test if data exists
    if (response.data) {
      if (response.data[requestName]) {
        return response.data[requestName];
      } // else we will drop out to false
    }
  }

  return false;
}

// COnverts underscores to spaces and spaces to underscores for interfacing with graph api
export function convertFromUIToGraphAPI(value: string): string {
  return value ? value.replace(/ /g, "_") : value;
}

export function convertFromGraphAPItoUI(value: string): string {
  return value ? value.replace(/_/g, " ") : value;
}

export function convertListFromUIToGraphAPI(listOfValues: string[]): string[] {
  if (Array.isArray(listOfValues)) {
    const listCopy = listOfValues.slice();
    return listCopy.map((value) => {
      return convertFromUIToGraphAPI(value);
    });
  }
  return listOfValues;
}

export function convertListFromGraphAPItoUI(listOfValues: string[]): string[] {
  if (!listOfValues) {
    return [];
  }

  if (Array.isArray(listOfValues)) {
    const listCopy = listOfValues.slice();
    return listCopy.map((value) => {
      return convertFromGraphAPItoUI(value);
    });
  }
  return listOfValues;
}

export function replaceSlashesWithDots(inputStr: string): string {
  return inputStr.replace(/\//g, ".");
}

export function replaceSlashesWithDashes(inputStr: string): string {
  return inputStr.replace(/\//g, "-");
}

// Construct Non Empty Graph Inputs
// ---------------------------------
// Takes an input (array or string or number)
// And returns null if the length of the input is 0
// Note that Dynamo wants nulls, not zero length elements
//
export function constructNonEmptyGraphInputs(
  inputDict: Record<string, any>
): Record<string, any> {
  const dict: Record<string, any> = {};
  for (const key in inputDict) {
    let newValue = inputDict[key];

    // If the input is an array then even if it has elements it needs to
    // Have non zero length elements
    if (null !== newValue) {
      if (Array.isArray(newValue)) {
        if (newValue.join().length > 0) {
          dict[key] = newValue;
        } else {
          dict[key] = null;
        }
      } else if (isNaN(newValue)) {
        // Otherwise if it is a string
        if (newValue.length > 0) {
          dict[key] = newValue;
        } else {
          dict[key] = null;
        }
      } else {
        // It was a number - add it
        dict[key] = newValue;
      }
    } else {
      dict[key] = null;
    }
  }

  return dict;
}

const hasHttpsProtocol = (url: string): boolean => {
  return /^https:\/\//i.test(url.trim());
};

const hasHttpProtocol = (url: string): boolean => {
  return /^http:\/\//i.test(url.trim());
};

// Ensures the URL has an https:// prefix.
// Adds https:// if no protocol is present.
// Leaves any existing protocol (including http://) unchanged — callers are
// responsible for rejecting http:// with an appropriate error message.
export const addProtocolIfMissing = (url: string): string => {
  const trimmedUrl = url.trim();
  if (!trimmedUrl) return trimmedUrl;
  if (hasHttpsProtocol(trimmedUrl) || hasHttpProtocol(trimmedUrl)) return trimmedUrl;
  return `https://${trimmedUrl}`;
};
